/* ============================================================
   HERCULES — lp-problem-chart

   Builds the curve from x:y pairs so the chart can change per product. A
   hardcoded SVG path cannot be edited by anyone not writing bezier by hand,
   which in practice means it never changes and every product gets the same
   chart - the exact failure this section has to avoid.

   Interpolation is monotone cubic (Fritsch-Carlson), NOT Catmull-Rom.
   Catmull-Rom overshoots between points, so a decline curve drawn through
   1.0, 0.5, 0.25 would bulge above 1.0 near the start - drawing a rise that
   the data does not contain. On a chart making a factual claim that is not a
   cosmetic problem.
   ============================================================ */
(function () {
  'use strict';

  var W = 900, H = 480;              /* viewBox, not pixels - it scales */
  var PAD = { t: 30, r: 46, b: 66, l: 78 };

  function parsePoints(raw) {
    if (!raw) return [];
    return raw.split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        if (bits.length !== 2) return null;
        var x = parseFloat(bits[0]);
        var y = parseFloat(bits[1]);
        if (isNaN(x) || isNaN(y)) return null;
        return { x: x, y: y };
      })
      .filter(Boolean)
      .sort(function (a, b) { return a.x - b.x; });
  }

  /* Fritsch-Carlson tangents: clamps slopes so the spline cannot overshoot
     between consecutive points. */
  function monotoneTangents(pts) {
    var n = pts.length;
    var d = [];   /* secant slopes */
    var m = [];   /* tangents */
    var i;

    for (i = 0; i < n - 1; i++) {
      var dx = pts[i + 1].x - pts[i].x;
      d[i] = dx === 0 ? 0 : (pts[i + 1].y - pts[i].y) / dx;
    }

    m[0] = d[0] || 0;
    for (i = 1; i < n - 1; i++) {
      if (!d[i - 1] || !d[i] || (d[i - 1] > 0) !== (d[i] > 0)) {
        m[i] = 0;                       /* local extremum - flatten it */
      } else {
        m[i] = (d[i - 1] + d[i]) / 2;
      }
    }
    m[n - 1] = d[n - 2] || 0;

    for (i = 0; i < n - 1; i++) {
      if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
      var a = m[i] / d[i];
      var b = m[i + 1] / d[i];
      var s = a * a + b * b;
      if (s > 9) {
        var t = 3 / Math.sqrt(s);
        m[i] = t * a * d[i];
        m[i + 1] = t * b * d[i];
      }
    }
    return m;
  }

  function buildPath(pts, sx, sy) {
    if (pts.length < 2) return '';
    var m = monotoneTangents(pts);
    var dAttr = 'M' + sx(pts[0].x).toFixed(2) + ',' + sy(pts[0].y).toFixed(2);

    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i], p1 = pts[i + 1];
      var dx = p1.x - p0.x;
      /* cubic Hermite -> bezier control points */
      var c1x = p0.x + dx / 3;
      var c1y = p0.y + (m[i] * dx) / 3;
      var c2x = p1.x - dx / 3;
      var c2y = p1.y - (m[i + 1] * dx) / 3;
      dAttr += ' C' + sx(c1x).toFixed(2) + ',' + sy(c1y).toFixed(2) +
               ' ' + sx(c2x).toFixed(2) + ',' + sy(c2y).toFixed(2) +
               ' ' + sx(p1.x).toFixed(2) + ',' + sy(p1.y).toFixed(2);
    }
    return dAttr;
  }

  /* Round a range out to sensible tick values rather than using the raw min
     and max, which give ticks like 0.147. */
  function niceTicks(min, max, count) {
    var span = max - min;
    if (span <= 0) return [min];
    var step = Math.pow(10, Math.floor(Math.log(span / count) / Math.LN10));
    var err = (span / count) / step;
    if (err >= 7.5) step *= 10;
    else if (err >= 3) step *= 5;
    else if (err >= 1.5) step *= 2;

    var out = [];
    var start = Math.ceil(min / step) * step;
    for (var v = start; v <= max + step * 0.001; v += step) {
      out.push(Math.round(v / step) * step);
    }
    return out;
  }

  function fmt(v, suffix) {
    var s = Math.abs(v) >= 10 || v === Math.round(v)
      ? String(Math.round(v))
      : String(Math.round(v * 100) / 100);
    return s + (suffix || '');
  }

  function el(tag, attrs, text) {
    var node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    Object.keys(attrs || {}).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    if (text != null) node.textContent = text;
    return node;
  }

  function render(section) {
    var canvas = section.querySelector('[data-problem-chart-canvas]');
    if (!canvas) return;

    var pts = parsePoints(section.getAttribute('data-points'));
    if (pts.length < 2) { canvas.remove(); return; }

    var xs = pts.map(function (p) { return p.x; });
    var ys = pts.map(function (p) { return p.y; });
    var xMin = Math.min.apply(null, xs), xMax = Math.max.apply(null, xs);
    var dataMin = Math.min.apply(null, ys), dataMax = Math.max.apply(null, ys);

    /* ---------- vertical scale ----------
       Default is FIT: the axis brackets the data with a small margin, so every
       curve runs corner to corner and the shape of the change is the thing you
       see first. A zero baseline flattens any series that moves inside a narrow
       band - 56% to 67% against a 0-70% axis is an eleven point rise drawn as a
       horizontal line, and the chart then contradicts the copy beside it.

       Truncating an axis exaggerates change, which is the standard way charts
       mislead. So whenever the floor is above zero the chart draws a break mark
       at the origin: the scale is declared rather than hidden.

       Two escapes. data-y-mode="zero" forces a zero baseline for a series where
       the distance to nothing is the point. data-y-min pins an exact floor when
       fit picks one you do not want. */
    var span = dataMax - dataMin;
    var pad = span > 0 ? span * 0.1 : (Math.abs(dataMax) * 0.1 || 1);

    var yMin, yMax = dataMax + pad;

    if (section.getAttribute('data-y-mode') === 'zero') {
      yMin = 0;
      yMax = dataMax;
    } else {
      yMin = dataMin - pad;
      /* Never invent a negative floor for data that cannot go below zero. */
      if (dataMin >= 0 && yMin < 0) yMin = 0;
    }

    var yMinAttr = section.getAttribute('data-y-min');
    if (yMinAttr !== null && yMinAttr !== '' && !isNaN(parseFloat(yMinAttr))) {
      var requested = parseFloat(yMinAttr);
      if (requested < dataMin) yMin = requested;
    }

    var truncated = yMin > 0;

    var sx = function (x) { return PAD.l + ((x - xMin) / (xMax - xMin)) * (W - PAD.l - PAD.r); };
    var sy = function (y) { return PAD.t + (1 - (y - yMin) / (yMax - yMin)) * (H - PAD.t - PAD.b); };

    var uid = 'pc' + Math.random().toString(36).slice(2, 8);
    var xLabel = section.getAttribute('data-x-label') || '';
    var yLabel = section.getAttribute('data-y-label') || '';
    var suffix = section.getAttribute('data-y-suffix') || '';
    var callout = section.getAttribute('data-callout') || '';
    var calloutX = parseFloat(section.getAttribute('data-callout-x'));

    var svg = el('svg', {
      viewBox: '0 0 ' + W + ' ' + H,
      role: 'img',
      'aria-labelledby': uid + 't ' + uid + 'd'
    });
    svg.appendChild(el('title', { id: uid + 't' }, yLabel || 'Chart'));

    /* Screen-reader description states the actual numbers. A chart that only
       exists visually says nothing to anyone using a reader. */
    var first = pts[0], last = pts[pts.length - 1];
    svg.appendChild(el('desc', { id: uid + 'd' },
      (yLabel || 'Value') + ' from ' + fmt(first.y, suffix) + ' at ' + first.x +
      ' to ' + fmt(last.y, suffix) + ' at ' + last.x + '.'));

    var defs = el('defs');
    var grad = el('linearGradient', { id: uid + 'f', x1: '0', y1: '0', x2: '0', y2: '1' });
    grad.appendChild(el('stop', { offset: '0%', 'stop-color': 'var(--plot)', 'stop-opacity': '0.13' }));
    grad.appendChild(el('stop', { offset: '100%', 'stop-color': 'var(--plot)', 'stop-opacity': '0' }));
    defs.appendChild(grad);
    svg.appendChild(defs);

    var yTicks = niceTicks(yMin, yMax, 5);
    var gGrid = el('g', { class: 'hs-c-grid' });
    yTicks.forEach(function (v) {
      if (Math.abs(v - yMin) < 1e-9) return;
      gGrid.appendChild(el('line', { x1: PAD.l, y1: sy(v), x2: W - PAD.r, y2: sy(v) }));
    });
    svg.appendChild(gGrid);

    var line = buildPath(pts, sx, sy);
    var base = H - PAD.b;
    svg.appendChild(el('path', {
      class: 'hs-c-fill',
      fill: 'url(#' + uid + 'f)',
      d: line + ' L' + sx(last.x).toFixed(2) + ',' + base + ' L' + sx(first.x).toFixed(2) + ',' + base + ' Z'
    }));

    var plot = el('path', { class: 'hs-c-plot', d: line });
    svg.appendChild(plot);

    svg.appendChild(el('line', { class: 'hs-c-axis', x1: PAD.l, y1: PAD.t, x2: PAD.l, y2: base }));
    svg.appendChild(el('line', { class: 'hs-c-axis', x1: PAD.l, y1: base, x2: W - PAD.r + 6, y2: base }));

    if (truncated) {
      /* Two strokes across the y axis just above the origin - the conventional
         mark for "this axis does not start at zero". */
      var by = base - 16;
      svg.appendChild(el('path', {
        class: 'hs-c-break',
        d: 'M' + (PAD.l - 7) + ',' + (by + 7) + ' L' + (PAD.l + 7) + ',' + (by - 1) +
           ' M' + (PAD.l - 7) + ',' + (by + 13) + ' L' + (PAD.l + 7) + ',' + (by + 5)
      }));
    }

    var gTick = el('g', { class: 'hs-c-tick' });
    yTicks.forEach(function (v) {
      gTick.appendChild(el('text', { x: PAD.l - 14, y: sy(v) + 5, 'text-anchor': 'end' }, fmt(v, suffix)));
    });
    pts.forEach(function (p) {
      gTick.appendChild(el('text', { x: sx(p.x), y: base + 28, 'text-anchor': 'middle' }, String(p.x)));
    });
    svg.appendChild(gTick);

    if (callout && !isNaN(calloutX)) {
      /* Interpolate the marker's y off the drawn curve rather than the nearest
         supplied point, so the dot sits ON the line at any x. */
      var y = valueAt(pts, calloutX);
      if (y != null) {
        svg.appendChild(el('line', { class: 'hs-c-mline', x1: PAD.l, y1: sy(y), x2: sx(calloutX), y2: sy(y) }));
        svg.appendChild(el('line', { class: 'hs-c-mline', x1: sx(calloutX), y1: sy(y), x2: sx(calloutX), y2: base }));
        svg.appendChild(el('circle', { class: 'hs-c-marker', cx: sx(calloutX), cy: sy(y), r: 6 }));
        var anchor = calloutX > (xMin + xMax) / 2 ? 'end' : 'start';
        svg.appendChild(el('text', {
          class: 'hs-c-callout',
          x: sx(calloutX) + (anchor === 'end' ? -16 : 16),
          y: sy(y) - 13,
          'text-anchor': anchor
        }, callout));
      }
    }

    if (xLabel) {
      svg.appendChild(el('text', {
        class: 'hs-c-label', x: PAD.l + (W - PAD.l - PAD.r) / 2, y: H - 14, 'text-anchor': 'middle'
      }, xLabel));
    }
    if (yLabel) {
      svg.appendChild(el('text', {
        class: 'hs-c-label',
        transform: 'translate(22,' + (PAD.t + (H - PAD.t - PAD.b) / 2) + ') rotate(-90)',
        'text-anchor': 'middle'
      }, yLabel));
    }

    canvas.innerHTML = '';
    canvas.appendChild(svg);

    maybeAnimate(section, plot);
  }

  function valueAt(pts, x) {
    if (x <= pts[0].x) return pts[0].y;
    if (x >= pts[pts.length - 1].x) return pts[pts.length - 1].y;
    var m = monotoneTangents(pts);
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i], p1 = pts[i + 1];
      if (x < p0.x || x > p1.x) continue;
      var h = p1.x - p0.x;
      var t = (x - p0.x) / h;
      var t2 = t * t, t3 = t2 * t;
      return (2 * t3 - 3 * t2 + 1) * p0.y +
             (t3 - 2 * t2 + t) * h * m[i] +
             (-2 * t3 + 3 * t2) * p1.y +
             (t3 - t2) * h * m[i + 1];
    }
    return null;
  }

  function maybeAnimate(section, plot) {
    if (section.getAttribute('data-animate') !== 'true') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof plot.getTotalLength !== 'function') return;

    var len;
    try { len = plot.getTotalLength(); } catch (e) { return; }
    if (!len) return;

    plot.style.strokeDasharray = len;
    plot.style.strokeDashoffset = len;
    section.classList.add('is-armed');

    var reveal = function () {
      plot.style.transition = 'stroke-dashoffset 1.5s cubic-bezier(.22,1,.36,1)';
      plot.style.strokeDashoffset = '0';
      section.classList.add('is-drawn');
    };

    if (!('IntersectionObserver' in window)) { reveal(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { reveal(); io.disconnect(); }
      });
    }, { threshold: 0.25 });
    io.observe(section);
  }

  /* ---------- scroll to the buy box ----------
     On a PDP the buy box is already on the page. Linking out to it is a
     navigation for something that is a viewport move, so the CTA scrolls. */

  /* A sticky header would otherwise sit on top of whatever we scroll to.
     Measured rather than hardcoded: the header height differs between
     templates and between mobile and desktop. */
  function stickyOffset() {
    var candidates = document.querySelectorAll(
      'header, .header-wrapper, .section-header, [data-sticky-header], .shopify-section-header-sticky'
    );
    var max = 0;
    Array.prototype.forEach.call(candidates, function (node) {
      var pos = window.getComputedStyle(node).position;
      if (pos !== 'sticky' && pos !== 'fixed') return;
      var rect = node.getBoundingClientRect();
      if (rect.top > 4) return;               /* not pinned to the top */
      if (rect.height > max) max = rect.height;
    });
    return max;
  }

  function bindScrollCta(section) {
    var btn = section.querySelector('[data-problem-chart-scroll]');
    if (!btn) return;

    var selector = btn.getAttribute('data-problem-chart-scroll') || '';

    btn.addEventListener('click', function () {
      /* Resolved on click, not at load. The buy box can be re-rendered by the
         time someone reaches this section - a variant change rebuilds it - so
         a node cached at page load may be detached by now. */
      var target = null;
      try { target = selector ? document.querySelector(selector) : null; } catch (e) { target = null; }

      /* No buy box on the page means this is a landing or collection page.
         Top of the page is where the offer is there too, so the button always
         does something rather than sometimes doing nothing. */
      var top = target
        ? target.getBoundingClientRect().top + window.pageYOffset - stickyOffset() - 16
        : 0;

      var reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });

      if (!target) return;

      /* Move focus too. Scrolling alone leaves a keyboard or screen reader
         user's position back at the button, so the next Tab returns them to
         the section they just asked to leave. */
      var focusTarget = target.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) || target;
      if (focusTarget === target && !target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      /* preventScroll so focusing does not fight the smooth scroll */
      try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-problem-chart]').forEach(function (s) {
      if (s.__pcDone) return;
      s.__pcDone = true;
      render(s);
      bindScrollCta(s);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.HerculesProblemChart = { init: init };
})();
